import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Link, useNavigate } from 'react-router-dom';
import { RxCross2, RxHamburgerMenu } from 'react-icons/rx';
import { openInNewTab } from '../utils/helpers/helpers';
import { cn } from '../utils/cn';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';

const ProHeader = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      data-testid='pro-header'
      className={cn(
        'sticky top-0 z-50 mx-auto max-w-screen transition-all duration-300',
        isScrolled ? 'bg-white shadow-md' : 'bg-background-brand-extra-bold'
      )}
    >
      <div
        className={cn(
          'container mx-auto flex h-18 flex-row items-center justify-between gap-x-3.5 transition-colors duration-300',
          isScrolled ? 'bg-white' : 'bg-background-brand-extra-bold'
        )}
      >
        <div className='flex w-full flex-row items-center'>
          <Link to='/home' title='HireMySub.com' className='flex-none pr-5'>
            <span className='sr-only'>HireMySub</span>
            <img
              src={
                isScrolled
                  ? 'https://cdn.hiremysub.com/hmsDefaultLogo.png'
                  : 'https://cdn.hiremysub.com/hmsLogoWhite.png'
              }
              className='w-40'
              alt='HireMySub Logo'
            />
          </Link>

          {/* Navigation Links */}
          <div className='desktop:flex desktop:gap-6 ml-8 hidden'>
            <Button
              variant='ghost'
              onClick={() => navigate('/pro/pricing')}
              title='Pricing'
              size='sm'
              aria-label='Pricing (navigates to pricing page)'
              className={cn(
                'border-none bg-transparent transition-colors duration-300 hover:bg-transparent hover:text-blue-500',
                isScrolled
                  ? 'hover:bg-background-brand-inverse hover:text-content-brand-hover'
                  : 'text-white hover:text-blue-200'
              )}
            >
              Pricing
            </Button>
          </div>
        </div>

        <div className='desktop:flex desktop:gap-5 hidden items-center'>
          <Button
            onClick={() => navigate('/')}
            size='sm'
            title='Hire a Sub'
            variant='ghost'
            aria-label='Hire a Sub (navigates to home page)'
            className={cn(
              'w-28 border-none bg-transparent transition-colors duration-300 hover:bg-transparent hover:text-blue-500',
              isScrolled
                ? 'hover:bg-background-brand-inverse hover:text-content-brand-hover'
                : 'text-white hover:text-blue-200'
            )}
          >
            Hire a Sub
          </Button>
          <Button
            variant={isScrolled ? 'inverse' : 'ghost'}
            role='link'
            title='Login'
            size='sm'
            aria-label='Login (opens in a new tab)'
            onClick={() => {
              if (import.meta.env.MODE !== 'production') {
                openInNewTab('http://localhost:3333/login');
              } else {
                openInNewTab('https://portal.hiremysub.com/login');
              }
            }}
            className={cn(
              'border-border-brand-inverse w-28 border transition-colors duration-300',
              !isScrolled &&
                'border-white text-white hover:bg-white hover:text-[var(--color-background-brand-extra-bold)]'
            )}
          >
            Login
          </Button>
          <Button
            variant={isScrolled ? 'brand' : 'accent'}
            onClick={() => navigate('/join')}
            title='Sign Up'
            size='sm'
            className={cn(
              'w-28 transition-colors duration-300',
              isScrolled ? '' : 'hover:bg-cyan-600'
            )}
            aria-label='Sign Up (navigates to sign up page)'
          >
            Sign Up
          </Button>
        </div>

        {/* Mobile Navigation */}
        <Popover as='div' className='desktop:hidden ml-auto'>
          {({ close }) => (
            <>
              <div className='flex items-center gap-2'>
                <Link
                  to='/hire'
                  className={cn(
                    'w-24 text-sm font-medium transition-colors duration-300',
                    isScrolled
                      ? 'text-gray-700 hover:text-blue-600'
                      : 'text-white hover:text-blue-200'
                  )}
                >
                  Hire a Sub
                </Link>
                <PopoverButton
                  className='cursor-pointer'
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  <span className='sr-only'>
                    {isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                  </span>
                  {isMobileMenuOpen ? (
                    <RxCross2
                      className='h-6 w-6'
                      aria-hidden='true'
                      color={isScrolled ? '#3B82F6' : '#ffffff'}
                    />
                  ) : (
                    <RxHamburgerMenu
                      className='h-6 w-6'
                      aria-hidden='true'
                      color={isScrolled ? '#3B82F6' : '#ffffff'}
                    />
                  )}
                </PopoverButton>
              </div>

              <PopoverPanel
                className='bg-background-brand-extra-bold fixed top-0 left-0 -z-50 max-h-full min-h-48 w-full overflow-y-auto overscroll-none rounded-b border-b shadow-lg'
                style={{ display: isMobileMenuOpen ? 'block' : 'none' }}
              >
                <div className='container mx-auto flex h-screen flex-col divide-y px-6'>
                  <div className='flex w-full flex-col items-center justify-center gap-3.5 pt-22'>
                    <Button
                      variant='ghost'
                      title='Pricing'
                      size='sm'
                      aria-label='Pricing (navigates to pricing page)'
                      onClick={() => {
                        navigate('/pro/pricing');
                      }}
                      className={cn(
                        'border-none bg-transparent text-center text-white transition-colors duration-300 hover:bg-transparent hover:text-blue-500'
                      )}
                    >
                      Pricing
                    </Button>
                    <Button
                      variant='inverse'
                      role='link'
                      aria-label='Login (opens in a new tab)'
                      onClick={() => {
                        if (import.meta.env.MODE !== 'production') {
                          openInNewTab('http://localhost:3333/login');
                        } else {
                          openInNewTab('https://portal.hiremysub.com/login');
                        }

                        close();
                        setIsMobileMenuOpen(false);
                      }}
                      className='mb-3.5 w-full uppercase'
                    >
                      Login
                    </Button>

                    <Button
                      variant='accent'
                      onClick={() => {
                        navigate('/join');
                        close();
                        setIsMobileMenuOpen(false);
                      }}
                      className='w-full uppercase'
                    >
                      Sign Up
                    </Button>
                  </div>
                </div>
              </PopoverPanel>
            </>
          )}
        </Popover>
      </div>
    </div>
  );
};

export default ProHeader;
